/**
 * ──────────────────────────────────────────────────────────────
 * @module   public/js/services/contactsStagingService.js
 * @summary  Helper for pushing and looking up “staged” contacts
 *           in Firestore (`/ContactsStaging`).
 *
 * ## Public API
 * • `uploadCsv(rows[])`      – push an array of raw contacts
 * • `get(contactId)`         – fetch a single staged contact by its doc ID

 * • `findByCrmId(id)`        – locate staged contact by CRM contact ID
 * • `update(id, fields)`     – update specific fields on a staged contact

 *
 * @author   Auto-Generated
 * @version  1.1.0
 * @since    2025-05-05
 * ──────────────────────────────────────────────────────────────
 */

import {
  serverTimestamp,
  getDoc,
  getDocs,
  query,
  where,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import {
  colWithTenant,
  docWithTenant,
  writeWithTenant,
} from '../utils/firestoreHelpers.js';
import { resolveRuntimeDi } from '../utils/runtimeDi.js';
import {
  extensionFieldForPhoneField,
  normalizePhoneNumberWithExtension,
  sanitizePhoneExtension,
} from '../utils/phoneExtension.js';

const GLOBAL_CONTACT_READ_ONLY_CODE = 'GLOBAL_CONTACT_READ_ONLY';
const PROTECTED_GLOBAL_SOURCE_FIELDS = new Set([
  'company',
  'domain',
  'companyWebsite',
  'companyIndustry',
  'companyEmployees',
  'companyCity',
  'companyState',
  'companyCountry',
  'jobTitle',
  'contactLocation',
  'contactEducation',
  'contactInterests',
  'companyEmployeeCount',
  'companyRevenue',
  'companySignals',
  'companySignalSummary',
  'topsail_analysis_contact_report_focusmode',
  'topsail_analysis_company_report_focusmode',
  'source',
  'provenance',
  'globalContactTenant',
]);

/**
 * Build the standard error used when a staged row sourced from GlobalContacts is mutated.
 *
 * @returns {Error} Read-only violation error instance.
 */
function buildGlobalProvenanceError() {
  const error = new Error(
    'Global contacts are managed centrally; edit the source record instead.'
  );
  error.code = GLOBAL_CONTACT_READ_ONLY_CODE;
  return error;
}

/**
 * Determine whether a payload attempts to mutate global-linkage provenance.
 *
 * @param {Record<string, unknown>} payload - Requested field updates.
 * @returns {boolean} True when lineage metadata is present in the payload.
 */
function hasGlobalProvenanceKeys(payload = {}) {
  return Object.keys(payload).some((key) => {
    if (key === 'data') return false;
    return key.startsWith('global');
  });
}

/**
 * Determine whether a payload mutates global-sourced fields that stay read-only
 * after placement into a tenant.
 *
 * @param {Record<string, unknown>} payload - Requested field updates.
 * @returns {boolean} True when the payload includes protected source fields.
 */
function hasProtectedGlobalSourceKeys(payload = {}) {
  return Object.keys(payload).some((key) => {
    if (key === 'data' || key.startsWith('global')) return false;
    return PROTECTED_GLOBAL_SOURCE_FIELDS.has(key);
  });
}

/**
 * Ensure callers only mutate tenant-owned fields on rows sourced from the
 * Global Contact Pool.
 *
 * @param {Record<string, unknown>|null} existing - Current Firestore payload.
 * @param {Record<string, unknown>} changes - Requested field updates.
 */
function assertGlobalEditAllowed(existing, changes = {}) {
  const globalId = existing?.globalContactId ?? null;

  if (!globalId) {
    if (
      hasGlobalProvenanceKeys(changes) ||
      (typeof changes.data === 'object' &&
        changes.data !== null &&
        hasGlobalProvenanceKeys(changes.data))
    ) {
      throw buildGlobalProvenanceError();
    }
    return;
  }

  if (
    hasGlobalProvenanceKeys(changes) ||
    (typeof changes.data === 'object' &&
      changes.data !== null &&
      hasGlobalProvenanceKeys(changes.data))
  ) {
    throw buildGlobalProvenanceError();
  }

  const topProtected = hasProtectedGlobalSourceKeys(changes);
  const nestedProtected =
    typeof changes.data === 'object' && changes.data !== null
      ? hasProtectedGlobalSourceKeys(changes.data)
      : false;

  if (topProtected || nestedProtected) {
    throw buildGlobalProvenanceError();
  }
}

/**
 * Build a Firestore payload that keeps contact fields at the top level.
 *
 * @param {Record<string, unknown>} payload - raw staging payload.
 * @returns {Record<string, unknown>} normalised payload ready for writes.
 */
function normalisePayload(payload = {}) {
  const { data: explicitData, ...rest } = payload;
  if (explicitData && typeof explicitData === 'object') {
    return {
      ...explicitData,
      ...rest,
    };
  }

  return { ...rest };
}

/**
 * Split explicit phone extensions in contact edit patches.
 *
 * @param {Record<string, unknown>} patch - Incoming field patch.
 * @returns {Record<string, unknown>} Patch with phone bases and extension fields.
 */
function normalizePhoneExtensionPatch(patch = {}) {
  const next = { ...(patch || {}) };
  const phoneFields = [
    'phone',
    'mobilePhone',
    'officePhone',
    'mainOfficePhone',
  ];

  for (const field of phoneFields) {
    const extField = extensionFieldForPhoneField(field);
    const hasPhone = Object.prototype.hasOwnProperty.call(next, field);
    const hasExtension = Object.prototype.hasOwnProperty.call(next, extField);

    if (hasPhone) {
      const raw = next[field];
      if (raw == null || String(raw).trim() === '') {
        next[field] = null;
        if (!hasExtension) next[extField] = null;
      } else {
        const parsed = normalizePhoneNumberWithExtension(raw);
        if (parsed.phone) next[field] = parsed.phone;
        else if (parsed.baseRaw) next[field] = parsed.baseRaw;
        if (parsed.extension && !hasExtension) {
          next[extField] = parsed.extension;
        }
      }
    }

    if (Object.prototype.hasOwnProperty.call(next, extField)) {
      const extension = sanitizePhoneExtension(next[extField]);
      next[extField] = extension || null;
    }
  }

  return next;
}

/**
 * Patch a ContactsStaging document resolved by the CRM contact ID.
 *
 * @param {object} params
 * @param {string} params.tenantId - Tenant to scope Firestore writes.
 * @param {string} params.contactId - CRM contact identifier.
 * @param {Record<string, unknown>} params.patch - Field updates to merge.
 * @param {{ setDocument?: (path: string, data: object, opts?: { merge?: boolean }) => Promise<void> }} params.fsService
 * @returns {Promise<{ stagingDocPath: string|null, stagingDocId: string|null }>} Updated doc path/id.
 */
export async function patchStagingSameAsContactEdit({
  tenantId,
  contactId,
  patch = {},
  services,
} = {}) {
  const normalizedPatch = normalizePhoneExtensionPatch(patch);
  if (
    !contactId ||
    !normalizedPatch ||
    Object.keys(normalizedPatch).length === 0
  ) {
    return { stagingDocPath: null };
  }

  const runtimeDi = resolveRuntimeDi();
  const stagingService =
    services?.contactsStagingService || runtimeDi.contactsStagingService;
  if (!stagingService) {
    return { stagingDocPath: null };
  }

  const resolvedTenantId =
    typeof tenantId === 'string' && tenantId.trim() ? tenantId.trim() : null;
  const existing = resolvedTenantId
    ? await stagingService.findByCrmId(contactId, {
        tenantId: resolvedTenantId,
      })
    : await stagingService.findByCrmId(contactId);
  let stagingDocId = null;

  if (existing?.id) {
    if (resolvedTenantId) {
      await stagingService.update(
        existing.id,
        { ...normalizedPatch },
        {
          tenantId: resolvedTenantId,
        }
      );
    } else {
      await stagingService.update(existing.id, { ...normalizedPatch });
    }
    stagingDocId = existing.id;
  } else {
    const payload = {
      crmContactId: contactId,
      ...normalizedPatch,
    };
    const created = resolvedTenantId
      ? await stagingService.create(payload, { tenantId: resolvedTenantId })
      : await stagingService.create(payload);
    stagingDocId = created?.id ?? null;
  }

  const stagingDocPath = stagingDocId
    ? `ContactsStaging/${stagingDocId}`
    : null;
  console.log('[FM][StagingPatch] path', {
    contactId,
    stagingDocPath,
    patch,
    normalizedPatch,
  });

  return { stagingDocPath };
}

/**
 * Persist tenant-owned contact edits while preserving Global Contact Pool
 * lineage on the staged row.
 *
 * @param {object} params
 * @param {string|null} [params.stagingDocId] - Existing ContactsStaging doc id.
 * @param {string|null} [params.contactLookupId] - CRM/contact id used to find or create a staged row.
 * @param {string|null} [params.contactMetaId] - Tenant ContactMeta/ContactsMeta key.
 * @param {string|null} [params.tenantId] - Explicit tenant for admin cross-tenant edits.
 * @param {Record<string, unknown>} [params.patch] - Editable contact field updates.
 * @param {{
 *   contactsStagingService?: ContactsStagingService,
 *   contactMetaService?: { update: (contactId: string, fields: Record<string, unknown>) => Promise<void> },
 * }} [params.services] - Dependency overrides.
 * @returns {Promise<{ stagingDocPath: string|null, contactMetaId: string|null }>} Persisted targets.
 */
export async function persistTenantOwnedContactEdit({
  stagingDocId = null,
  contactLookupId = null,
  contactMetaId = null,
  tenantId = null,
  patch = {},
  services,
} = {}) {
  const normalizedPatch = normalizePhoneExtensionPatch(patch);
  if (!normalizedPatch || Object.keys(normalizedPatch).length === 0) {
    return {
      stagingDocPath: null,
      contactMetaId:
        typeof contactMetaId === 'string' && contactMetaId.trim()
          ? contactMetaId.trim()
          : null,
    };
  }

  const runtimeDi = resolveRuntimeDi();
  const stagingService =
    services?.contactsStagingService || runtimeDi.contactsStagingService;
  const metaService =
    services?.contactMetaService || runtimeDi.contactMetaService;
  const resolvedStagingDocId =
    typeof stagingDocId === 'string' && stagingDocId.trim()
      ? stagingDocId.trim()
      : null;
  const resolvedLookupId =
    typeof contactLookupId === 'string' && contactLookupId.trim()
      ? contactLookupId.trim()
      : null;
  const resolvedMetaId =
    typeof contactMetaId === 'string' && contactMetaId.trim()
      ? contactMetaId.trim()
      : resolvedLookupId;
  const resolvedTenantId =
    typeof tenantId === 'string' && tenantId.trim() ? tenantId.trim() : null;

  let stagingDocPath = null;
  if (stagingService) {
    if (resolvedStagingDocId) {
      if (resolvedTenantId) {
        await stagingService.update(resolvedStagingDocId, normalizedPatch, {
          tenantId: resolvedTenantId,
        });
      } else {
        await stagingService.update(resolvedStagingDocId, normalizedPatch);
      }
      stagingDocPath = `ContactsStaging/${resolvedStagingDocId}`;
    } else if (resolvedLookupId) {
      const result = await patchStagingSameAsContactEdit({
        tenantId: resolvedTenantId,
        contactId: resolvedLookupId,
        patch: normalizedPatch,
        services,
      });
      stagingDocPath = result?.stagingDocPath ?? null;
    }
  }

  if (resolvedMetaId && metaService) {
    if (resolvedTenantId) {
      await metaService.update(resolvedMetaId, normalizedPatch, {
        tenantId: resolvedTenantId,
      });
    } else {
      await metaService.update(resolvedMetaId, normalizedPatch);
    }
  }

  return { stagingDocPath, contactMetaId: resolvedMetaId };
}

export default class ContactsStagingService {
  #services;

  /**
   * Create a ContactsStaging helper using optional dependency overrides.
   *
   * @param {Record<string, unknown>|null} [services=null] - Runtime service bag.
   */
  constructor(services = null) {
    this.#services = services;
    this.collectionPath = 'ContactsStaging';
  }

  /**
   * Resolve the AuthService for tenant-scoped staging operations.
   *
   * @returns {{ currentUser?: object, waitForLogin?: Function }|null} AuthService.
   */
  #auth() {
    return resolveRuntimeDi(this.#services).auth || null;
  }

  /**
   * Resolve the active tenant from explicit options or the current user.
   *
   * @param {{ tenantId?: string }} [options] - Optional explicit tenant scope.
   * @returns {Promise<string>} Tenant identifier.
   */
  async #tenantId(options = {}) {
    if (typeof options?.tenantId === 'string' && options.tenantId.trim()) {
      return options.tenantId.trim();
    }
    const auth = this.#auth();
    const user = auth?.currentUser ?? (await auth?.waitForLogin?.());
    const tenantId = user?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    return tenantId;
  }

  /**
   * Push an array of contacts into `/ContactsStaging`.
   * @param {Array<object>} rows – array of plain objects
   * @returns {Promise<number>}  number of docs written
   */
  async uploadCsv(rows = []) {
    let written = 0;
    const tenantId = await this.#tenantId();
    for (const row of rows) {
      await writeWithTenant(
        tenantId,
        docWithTenant(
          tenantId,
          `${this.collectionPath}/${crypto.randomUUID()}`
        ),
        {
          ...row,
          status: 'PENDING',
          createdAt: serverTimestamp(),
        }
      );
      written++;
    }
    return written;
  }

  /**
   * Fetch a single staged contact by its Firestore doc ID.
   * @param   {string} contactId
   * @returns {Promise<object|null>}  doc data or null if not found
   */
  async get(contactId) {
    if (!contactId) return null;
    const tenantId = await this.#tenantId();
    const ref = docWithTenant(tenantId, `${this.collectionPath}/${contactId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  }

  /**
   * Look up a staged contact by its CRM contact ID.
   * @param   {string} crmContactId - CRM contact identifier.
   * @param   {{ tenantId?: string }} [options] - Optional explicit tenant scope.
   * @returns {Promise<{id: string, data: object}|null>} id and data if found.
   */
  async findByCrmId(crmContactId, options = {}) {
    if (!crmContactId) return null;

    const tenantId = await this.#tenantId(options);
    const directRef = docWithTenant(
      tenantId,
      `${this.collectionPath}/${crmContactId}`
    );
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      return {
        id: crmContactId,
        data: directSnap.data(),
      };
    }

    const col = colWithTenant(tenantId, this.collectionPath);
    let q = query(col, where('crmContactId', '==', crmContactId));
    let snap = await getDocs(q);
    if (snap.empty || snap.docs.length === 0) {
      q = query(col, where('contactId', '==', crmContactId));
      snap = await getDocs(q);
    }
    const doc = snap.docs[0];
    return doc ? { id: doc.id, data: doc.data() } : null;
  }

  /**
   * Create a new staged contact document.
   *
   * @param {Record<string, unknown>} payload - Firestore document payload.
   * @param {{ tenantId?: string }} [options] - Optional explicit tenant scope.
   * @returns {Promise<{ id: string, data: Record<string, unknown> }>} Created doc info.
   */
  async create(payload = {}, options = {}) {
    const tenantId = await this.#tenantId(options);

    assertGlobalEditAllowed(null, payload);
    const docId = crypto.randomUUID();
    const ref = docWithTenant(tenantId, `${this.collectionPath}/${docId}`);
    const docPayload = normalisePayload(payload);
    await writeWithTenant(tenantId, ref, docPayload);
    return { id: docId, data: docPayload };
  }

  /**
   * Update fields on a staged contact document.
   * @param {string} id - Firestore document ID.
   * @param {Record<string, unknown>} fields - fields to update.
   * @param {{ tenantId?: string }} [options] - Optional explicit tenant scope.
   * @returns {Promise<void>}
   */
  async update(id, fields = {}, options = {}) {
    if (!id) return;
    const tenantId = await this.#tenantId(options);
    const ref = docWithTenant(tenantId, `${this.collectionPath}/${id}`);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : null;
    assertGlobalEditAllowed(existing, fields);
    const payload = normalisePayload(fields);
    await writeWithTenant(tenantId, ref, payload, { merge: true });
  }

  /**
   * Create or update a staged contact document scoped by CRM Contact ID.
   * Ensures overrides (including null tombstones) persist without relying on
   * fallback lookups.
   *
   * @param {string} crmContactId - CRM contact identifier.
   * @param {Record<string, unknown>} data - Contact field overrides.
   * @returns {Promise<{ stagingDocPath: string|null, stagingDocId: string|null }>}
   */
  async createOrUpdateForCrm(crmContactId, data = {}) {
    if (!crmContactId) return;
    return patchStagingSameAsContactEdit({
      contactId: crmContactId,
      patch: data,
      services: { contactsStagingService: this },
    });
  }
}
